import { ArrowUp, Check, ConciergeBell, Headphones, Loader2, Plug, ShieldAlert, Star } from 'lucide-react';
import { useEffect, useState } from 'react';
import { t } from '@/lib/i18n';
import { cn } from '@/lib/utils';

const PROMPT = t('We sell on Amazon. Take care of our customer cases and get us more reviews.');

type StepState = 'todo' | 'doing' | 'done';

const BUILD_STEPS = [
  { doing: t('Setting up Customer Support …'), done: t('Customer Support ready'), detail: t('Lena leads, Mia and Max join') },
  { doing: t('Connecting the tools …'), done: t('Tools connected'), detail: t('Helpdesk, Shop') },
  { doing: t('Setting up Reviews …'), done: t('Reviews ready'), detail: t('Ben leads, Ida joins') },
  { doing: t('Handing out the first work …'), done: t('Work handed out'), detail: t('12 open cases → Customer Support') },
];

/**
 * Stages: 0 typing · 1 sent · 2 VICKI thinks · 3 her proposal · 4 the approval card · 5 approved ·
 * 6–9 one build step each · 10 her report.
 */
const PROPOSAL = 3;
const CARD = 4;
const APPROVED = 5;
const FIRST_BUILD = 6;
const FINAL = FIRST_BUILD + BUILD_STEPS.length;
const TYPE_MS = 28;
const STAGE_MS: Record<number, number> = { 1: 600, 2: 1100, [PROPOSAL]: 1500, [CARD]: 1500, [APPROVED]: 700, [FINAL]: 3200 };
const BUILD_STEP_MS = 1100;

/**
 * Landing-page mock of the Front Desk (docs/features/front-desk.md): a person tells VICKI what they want, she proposes
 * two desks, the person approves, she builds the teams and hands the first work out. Calls `onDone` after her report.
 * `still` renders the finished state without animation.
 */
export function VickiBuilder({ onDone, still, className }: { onDone?: () => void; still?: boolean; className?: string }) {
  const [typed, setTyped] = useState(still ? PROMPT.length : 0);
  const [stage, setStage] = useState(still ? FINAL : 0);

  useEffect(() => {
    if (still) return;
    if (stage === 0) {
      if (typed < PROMPT.length) {
        const timer = setTimeout(() => setTyped((n) => n + 1), TYPE_MS + (PROMPT[typed] === ' ' ? 25 : 0));
        return () => clearTimeout(timer);
      }
      const timer = setTimeout(() => setStage(1), 400);
      return () => clearTimeout(timer);
    }
    const timer = setTimeout(() => (stage === FINAL ? onDone?.() : setStage(stage + 1)), STAGE_MS[stage] ?? BUILD_STEP_MS);
    return () => clearTimeout(timer);
  }, [still, stage, typed, onDone]);

  const step = (i: number): StepState => (stage < FIRST_BUILD + i ? 'todo' : stage === FIRST_BUILD + i ? 'doing' : 'done');
  const [support, tools, reviews, work] = BUILD_STEPS.map((_, i) => step(i));

  return (
    <div className={cn('relative rounded-2xl border bg-card/80 p-1.5 shadow-2xl shadow-primary/10 backdrop-blur', className)} role="img" aria-label={t('Example: VICKI builds two desks from one sentence')}>
      <div className="overflow-hidden rounded-xl border bg-background">
        {/* Header */}
        <div className="flex items-center gap-2 border-b px-4 py-3">
          <ConciergeBell className="size-4 text-primary" />
          <span className="text-sm font-semibold">{t('Front Desk')}</span>
          <span className="text-xs text-muted-foreground">· VICKI</span>
          <span className="ml-auto text-xs text-muted-foreground">
            {stage === FINAL ? t('Running') : stage >= FIRST_BUILD ? t('Building …') : stage >= 2 ? t('Thinking with you') : t('New chat')}
          </span>
        </div>

        {/* Chat */}
        {/* A fixed height, newest at the bottom: the card doesn't jump while the chat grows. */}
        <div className="flex h-[216px] flex-col justify-end gap-2 overflow-hidden border-b px-4 py-3">
          {stage === 0 ? (
            <div className="flex items-end gap-2 rounded-lg border bg-card px-3 py-2">
              <p className="min-h-10 flex-1 text-[13px] leading-5">
                {PROMPT.slice(0, typed)}
                <span className="ml-px inline-block h-4 w-[2px] translate-y-[3px] animate-pulse bg-primary" />
              </p>
              <span className={cn('flex size-7 shrink-0 items-center justify-center rounded-md transition-colors', typed === PROMPT.length ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground')}>
                <ArrowUp className="size-4" />
              </span>
            </div>
          ) : (
            <div className="flex justify-end">
              <p className="animate-in fade-in slide-in-from-bottom-2 max-w-[88%] rounded-2xl rounded-br-sm bg-primary px-3.5 py-2 text-[13px] leading-5 text-primary-foreground duration-300">{PROMPT}</p>
            </div>
          )}
          {stage === 2 && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <VickiDot /> <Loader2 className="size-3 animate-spin" /> {t('VICKI is looking at your business …')}
            </div>
          )}
          {stage >= PROPOSAL && stage < FINAL && (
            <div className="animate-in fade-in slide-in-from-bottom-2 flex items-start gap-2 duration-300">
              <VickiDot />
              <p className="max-w-[88%] rounded-2xl rounded-tl-sm border bg-card px-3 py-2 text-[13px] leading-5">
                {t('I’d set up two desks: Customer Support answers every case within 48 hours, Reviews asks happy customers for a review. Shall I build them?')}
              </p>
            </div>
          )}
          {stage >= CARD && stage < FIRST_BUILD && (
            <div
              className={cn(
                'animate-in fade-in slide-in-from-bottom-2 flex items-center gap-2 rounded-lg border-2 px-3 py-2 text-xs duration-300',
                stage === APPROVED
                  ? 'border-emerald-300 bg-emerald-50 dark:border-emerald-800 dark:bg-emerald-950/30'
                  : 'border-orange-300 bg-orange-50/70 dark:border-orange-800 dark:bg-orange-950/30',
              )}
            >
              <ShieldAlert className={cn('size-4 shrink-0', stage === APPROVED ? 'text-emerald-600' : 'text-orange-600')} />
              <span className="flex-1 font-medium">{t('Build 2 desks: Customer Support, Reviews')}</span>
              <span
                className={cn(
                  'inline-flex h-6 items-center gap-1 rounded-md px-2 font-medium transition-all',
                  stage === APPROVED ? 'scale-95 bg-emerald-600 text-white' : 'bg-emerald-600 text-white shadow-[0_0_0_3px] shadow-emerald-500/30',
                )}
              >
                <Check className="size-3" /> {stage === APPROVED ? t('Approved') : t('Approve')}
              </span>
            </div>
          )}
          {stage === FINAL && (
            <div className="animate-in fade-in slide-in-from-bottom-2 flex items-start gap-2 duration-300">
              <VickiDot />
              <p className="max-w-[88%] rounded-2xl rounded-tl-sm border bg-card px-3 py-2 text-[13px] leading-5">
                {t('Both desks are running. Lena is on the 12 open cases; I’ll tell you here when they are answered.')}
              </p>
            </div>
          )}
        </div>

        {/* The desks she builds */}
        <div className="grid grid-cols-2 gap-2 px-4 py-3">
          <DeskPanel icon={Headphones} name={t('Customer Support')} state={support} lead="Lena" team={['Mia', 'Max']} tools={tools === 'todo' ? [] : [t('Helpdesk'), t('Shop')]} badge={work === 'done' ? t('12 cases') : null} />
          <DeskPanel icon={Star} name={t('Product reviews')} state={reviews} lead="Ben" team={['Ida']} tools={[]} badge={null} />
        </div>

        {/* Build log */}
        <ul className="space-y-1 border-t bg-muted/40 px-4 py-2.5">
          {BUILD_STEPS.map((s, i) => {
            const state = step(i);
            return (
              <li key={s.done} className={cn('flex items-center gap-2 text-xs transition-opacity duration-300', state === 'todo' ? 'opacity-35' : 'opacity-100')}>
                <span
                  className={cn(
                    'flex size-4 shrink-0 items-center justify-center rounded-full border transition-colors',
                    state === 'done' && 'border-emerald-500 bg-emerald-500 text-white',
                    state === 'doing' && 'border-primary text-primary',
                  )}
                >
                  {state === 'done' ? <Check className="size-2.5" strokeWidth={3} /> : state === 'doing' ? <Loader2 className="size-2.5 animate-spin" /> : null}
                </span>
                <span className={cn('font-medium', state === 'doing' && 'text-primary')}>{state === 'doing' ? s.doing : s.done}</span>
                {state === 'done' && <span className="animate-in fade-in truncate text-muted-foreground duration-500">· {s.detail}</span>}
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}

function VickiDot() {
  return (
    <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-primary to-violet-500 text-[10px] font-bold text-white" aria-hidden>
      V
    </span>
  );
}

function DeskPanel({
  icon: Icon,
  name,
  state,
  lead,
  team,
  tools,
  badge,
}: {
  icon: typeof Star;
  name: string;
  state: StepState;
  lead: string;
  team: string[];
  tools: string[];
  badge: string | null;
}) {
  const built = state !== 'todo';
  return (
    <div
      className={cn(
        'relative flex h-[132px] flex-col rounded-xl border p-2.5 transition-all duration-700',
        state === 'done' ? 'border-primary/40 bg-primary/[0.04] shadow-[0_0_0_3px] shadow-primary/10' : built ? 'border-primary/30' : 'border-dashed bg-muted/30',
      )}
    >
      <div className={cn('flex items-center gap-1.5 text-xs font-semibold transition-all duration-500', built ? 'opacity-100' : 'opacity-40')}>
        <Icon className="size-3.5 text-primary" /> <span className="truncate">{name}</span>
        {badge && <span className="animate-in fade-in ml-auto rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800 duration-500 dark:bg-amber-950 dark:text-amber-300">{badge}</span>}
      </div>
      {!built && <div className="flex flex-1 items-center justify-center text-[11px] text-muted-foreground">{t('Desk appears here')}</div>}
      {built && (
        <div className="mt-2 flex flex-1 flex-col gap-1.5">
          <Person name={lead} role={t('Lead')} lead state={state} />
          <div className="flex flex-wrap gap-1">
            {team.map((p, i) => (
              <Person key={p} name={p} state={state === 'done' ? 'done' : 'todo'} delay={i * 150} />
            ))}
          </div>
          {tools.length > 0 && (
            <div className="mt-auto flex gap-1">
              {tools.map((tool) => (
                <span key={tool} className="animate-in fade-in inline-flex items-center gap-1 rounded border border-emerald-300/70 bg-emerald-50 px-1.5 py-0.5 text-[10px] font-medium text-emerald-800 duration-500 dark:border-emerald-800/60 dark:bg-emerald-950/40 dark:text-emerald-300">
                  <Plug className="size-2.5" /> {tool}
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Person({ name, role, lead, state, delay = 0 }: { name: string; role?: string; lead?: boolean; state: StepState; delay?: number }) {
  return (
    <span
      style={{ transitionDelay: state === 'todo' ? '0ms' : `${delay}ms` }}
      className={cn(
        'inline-flex w-fit items-center gap-1.5 rounded-full border bg-card py-0.5 pr-2 pl-0.5 transition-all duration-500 ease-[cubic-bezier(.34,1.56,.64,1)]',
        state === 'todo' && !lead ? 'scale-50 opacity-0' : 'scale-100 opacity-100',
        lead && 'border-primary/50 ring-2 ring-primary/10',
      )}
    >
      <span className="relative flex size-5 items-center justify-center rounded-full bg-gradient-to-br from-primary to-violet-500 text-[10px] font-semibold text-white">
        {name[0]}
        {lead && state === 'doing' && <span className="absolute inset-0 animate-ping rounded-full bg-primary/40" />}
      </span>
      <span className="text-[11px] font-semibold">{name}</span>
      {role && <span className="text-[10px] text-muted-foreground">{role}</span>}
    </span>
  );
}
